import { Extension, Mark, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { ReplaceStep, ReplaceAroundStep } from "@tiptap/pm/transform";

// ============================================
// Insertion Mark
// ============================================
export interface InsertionMarkOptions {
  HTMLAttributes: Record<string, any>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    insertionMark: {
      setInsertion: (changeId: string) => ReturnType;
      unsetInsertion: () => ReturnType;
    };
  }
}

export const InsertionMark = Mark.create<InsertionMarkOptions>({
  name: "insertion",

  addOptions() {
    return {
      HTMLAttributes: {
        class: "track-change-insertion",
      },
    };
  },

  addAttributes() {
    return {
      changeId: {
        default: null,
        parseHTML: element => element.getAttribute("data-change-id"),
        renderHTML: attributes => {
          if (!attributes.changeId) return {};
          return { "data-change-id": attributes.changeId };
        },
      },
      author: {
        default: null,
        parseHTML: element => element.getAttribute("data-change-author"),
        renderHTML: attributes => {
          if (!attributes.author) return {};
          return { "data-change-author": attributes.author };
        },
      },
      date: {
        default: null,
        parseHTML: element => element.getAttribute("data-change-date"),
        renderHTML: attributes => {
          if (!attributes.date) return {};
          return { "data-change-date": attributes.date };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-change-type="insertion"]',
      },
      {
        tag: "span.track-change-insertion",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-change-type": "insertion",
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setInsertion:
        (changeId: string) =>
        ({ commands }) => {
          return commands.setMark(this.name, { changeId });
        },
      unsetInsertion:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name);
        },
    };
  },
});

// ============================================
// Deletion Mark
// ============================================
export interface DeletionMarkOptions {
  HTMLAttributes: Record<string, any>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    deletionMark: {
      setDeletion: (changeId: string) => ReturnType;
      unsetDeletion: () => ReturnType;
    };
  }
}

export const DeletionMark = Mark.create<DeletionMarkOptions>({
  name: "deletion",

  // IMPORTANT: Don't extend deletion marks when typing at their boundaries.
  // Without this, typing adjacent to deleted text causes new characters to
  // inherit the deletion mark, resulting in both insertion + deletion marks
  // on the same character (green underline + red strikethrough).
  inclusive: false,

  addOptions() {
    return {
      HTMLAttributes: {
        class: "track-change-deletion",
      },
    };
  },

  addAttributes() {
    return {
      changeId: {
        default: null,
        parseHTML: element => element.getAttribute("data-change-id"),
        renderHTML: attributes => {
          if (!attributes.changeId) return {};
          return { "data-change-id": attributes.changeId };
        },
      },
      author: {
        default: null,
        parseHTML: element => element.getAttribute("data-change-author"),
        renderHTML: attributes => {
          if (!attributes.author) return {};
          return { "data-change-author": attributes.author };
        },
      },
      date: {
        default: null,
        parseHTML: element => element.getAttribute("data-change-date"),
        renderHTML: attributes => {
          if (!attributes.date) return {};
          return { "data-change-date": attributes.date };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-change-type="deletion"]',
      },
      {
        tag: "span.track-change-deletion",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-change-type": "deletion",
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setDeletion:
        (changeId: string) =>
        ({ commands }) => {
          return commands.setMark(this.name, { changeId });
        },
      unsetDeletion:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name);
        },
    };
  },
});

// ============================================
// Track Changes Extension (combines both marks + commands)
// ============================================
export interface TrackChangesOptions {
  /** The current user's display name. Stored on every insertion/deletion mark. */
  author?: string;
  onChangeActivated?: (
    changeId: string | null,
    type: "insertion" | "deletion" | null,
    author?: string | null,
    date?: string | null
  ) => void;
  onTrackingStateChanged?: (enabled: boolean) => void;
}

export interface TrackChangeInfo {
  id: string;
  type: "insertion" | "deletion";
  from: number;
  to: number;
  text: string;
  author?: string;
  date?: string;
}

// Plugin key for track changes state
const trackChangesPluginKey = new PluginKey("trackChanges");
const trackChangesNavPluginKey = new PluginKey("trackChangesNavigation");

const GROUPING_TIMEOUT_MS = 2000; // Group insertions within 2 seconds
const DELETION_GROUPING_TIMEOUT_MS = 1000; // Shorter timeout for deletions
// Maximum distance (in characters) between deletions to still group them.
// User backspace/delete presses produce adjacent positions; AI operations
// at different document locations are far apart and should not be grouped.
const DELETION_PROXIMITY_THRESHOLD = 2;

// Generate a unique change ID
function generateChangeId(): string {
  return `change-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

// All grouping state lives in TrackChangesStorage (per editor instance).
// The functions below accept the storage object to avoid global mutable state.

interface TrackChangesStorage {
  enabled: boolean;
  suppressMarkStripping: boolean;
  author: string | undefined;
  // Insertion grouping
  currentInsertionChangeId: string | null;
  lastInsertionEndPos: number;
  lastInsertionTime: number;
  // Deletion grouping
  currentDeletionChangeId: string | null;
  lastDeletionTime: number;
  lastDeletionPos: number;
}

function getInsertionChangeId(storage: TrackChangesStorage, insertStart: number): string {
  const now = Date.now();
  const timeSinceLastInsert = now - storage.lastInsertionTime;
  const isContinuous =
    storage.currentInsertionChangeId !== null &&
    insertStart === storage.lastInsertionEndPos &&
    timeSinceLastInsert < GROUPING_TIMEOUT_MS;

  if (isContinuous) {
    return storage.currentInsertionChangeId!;
  } else {
    storage.currentInsertionChangeId = generateChangeId();
    return storage.currentInsertionChangeId;
  }
}

function updateInsertionTracking(storage: TrackChangesStorage, endPos: number) {
  storage.lastInsertionEndPos = endPos;
  storage.lastInsertionTime = Date.now();
}

function resetInsertionTracking(storage: TrackChangesStorage) {
  storage.currentInsertionChangeId = null;
  storage.lastInsertionEndPos = -1;
  storage.lastInsertionTime = 0;
}

function getDeletionChangeId(storage: TrackChangesStorage, deletePos: number): string {
  const now = Date.now();
  const timeSinceLastDelete = now - storage.lastDeletionTime;
  const isNearby =
    storage.lastDeletionPos >= 0 &&
    Math.abs(deletePos - storage.lastDeletionPos) <= DELETION_PROXIMITY_THRESHOLD;
  const isContinuous =
    storage.currentDeletionChangeId !== null &&
    timeSinceLastDelete < DELETION_GROUPING_TIMEOUT_MS &&
    isNearby;

  if (isContinuous) {
    return storage.currentDeletionChangeId!;
  } else {
    storage.currentDeletionChangeId = generateChangeId();
    return storage.currentDeletionChangeId;
  }
}

function updateDeletionTracking(storage: TrackChangesStorage, deletePos: number) {
  storage.lastDeletionTime = Date.now();
  storage.lastDeletionPos = deletePos;
}

function resetAllTracking(storage: TrackChangesStorage) {
  storage.currentInsertionChangeId = null;
  storage.lastInsertionEndPos = -1;
  storage.lastInsertionTime = 0;
  storage.currentDeletionChangeId = null;
  storage.lastDeletionTime = 0;
  storage.lastDeletionPos = -1;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    trackChanges: {
      enableTrackChanges: () => ReturnType;
      disableTrackChanges: () => ReturnType;
      toggleTrackChanges: () => ReturnType;
      acceptChange: (changeId: string) => ReturnType;
      rejectChange: (changeId: string) => ReturnType;
      acceptAllChanges: () => ReturnType;
      rejectAllChanges: () => ReturnType;
      setTrackChangesAuthor: (author: string) => ReturnType;
    };
  }
}

export const TrackChangesExtension = Extension.create<TrackChangesOptions>({
  name: "trackChanges",

  addOptions() {
    return {
      author: undefined,
      onChangeActivated: undefined,
      onTrackingStateChanged: undefined,
    };
  },

  addStorage(): TrackChangesStorage {
    return {
      enabled: false,
      // When true, appendTransaction skips mark-stripping entirely.
      // Used during programmatic setContent() calls so that follow-up
      // transactions from other extensions (e.g. pagination restructuring)
      // don't accidentally strip track-change marks.
      suppressMarkStripping: false,
      // Current author name, set from options or via setTrackChangesAuthor command
      author: undefined as string | undefined,
      // Insertion grouping (per-editor, not global)
      currentInsertionChangeId: null,
      lastInsertionEndPos: -1,
      lastInsertionTime: 0,
      // Deletion grouping (per-editor, not global)
      currentDeletionChangeId: null,
      lastDeletionTime: 0,
      lastDeletionPos: -1,
    };
  },

  onCreate() {
    // Initialize storage author from options
    if (this.options.author) {
      this.storage.author = this.options.author;
    }
  },

  addCommands() {
    const extensionThis = this;

    return {
      enableTrackChanges:
        () =>
        ({ editor }) => {
          const storage = (editor.storage as any).trackChanges;
          if (storage) {
            storage.enabled = true;
          }
          if (extensionThis.options.onTrackingStateChanged) {
            extensionThis.options.onTrackingStateChanged(true);
          }
          return true;
        },

      disableTrackChanges:
        () =>
        ({ editor }) => {
          const storage = (editor.storage as any).trackChanges as TrackChangesStorage;
          if (storage) {
            storage.enabled = false;
            resetAllTracking(storage);
          }
          if (extensionThis.options.onTrackingStateChanged) {
            extensionThis.options.onTrackingStateChanged(false);
          }
          return true;
        },

      toggleTrackChanges:
        () =>
        ({ editor }) => {
          const storage = (editor.storage as any).trackChanges;
          if (storage) {
            storage.enabled = !storage.enabled;
          }
          if (extensionThis.options.onTrackingStateChanged) {
            extensionThis.options.onTrackingStateChanged(
              storage?.enabled || false
            );
          }
          return true;
        },

      acceptChange:
        (changeId: string) =>
        ({ tr, state, dispatch }) => {
          const { doc } = state;

          if (dispatch) {
            // Collect all ranges to modify (in reverse order to handle position shifts)
            const changes: {
              pos: number;
              size: number;
              type: string;
              markType: any;
            }[] = [];

            doc.descendants((node, pos) => {
              node.marks.forEach(mark => {
                if (mark.attrs.changeId === changeId) {
                  changes.push({
                    pos,
                    size: node.nodeSize,
                    type: mark.type.name,
                    markType: mark.type,
                  });
                }
              });
            });

            if (changes.length > 0) {
              // Mark this transaction so onTransaction doesn't re-process deletions
              tr.setMeta("trackManualChanged", true);

              // Process in reverse order to handle position shifts correctly
              changes.sort((a, b) => b.pos - a.pos);

              for (const change of changes) {
                if (change.type === "insertion") {
                  // Accept insertion: just remove the mark, keep the text
                  tr.removeMark(
                    change.pos,
                    change.pos + change.size,
                    change.markType
                  );
                } else if (change.type === "deletion") {
                  // Accept deletion: remove the text entirely
                  tr.delete(change.pos, change.pos + change.size);
                }
              }

              dispatch(tr);
            }
          }

          return true;
        },

      rejectChange:
        (changeId: string) =>
        ({ tr, state, dispatch }) => {
          const { doc } = state;

          if (dispatch) {
            // Collect all ranges to modify (in reverse order to handle position shifts)
            const changes: {
              pos: number;
              size: number;
              type: string;
              markType: any;
            }[] = [];

            doc.descendants((node, pos) => {
              node.marks.forEach(mark => {
                if (mark.attrs.changeId === changeId) {
                  changes.push({
                    pos,
                    size: node.nodeSize,
                    type: mark.type.name,
                    markType: mark.type,
                  });
                }
              });
            });

            if (changes.length > 0) {
              // Mark this transaction so onTransaction doesn't re-process deletions
              tr.setMeta("trackManualChanged", true);

              // Process in reverse order to handle position shifts correctly
              changes.sort((a, b) => b.pos - a.pos);

              for (const change of changes) {
                if (change.type === "insertion") {
                  // Reject insertion: remove the text
                  tr.delete(change.pos, change.pos + change.size);
                } else if (change.type === "deletion") {
                  // Reject deletion: just remove the mark, keep the text
                  tr.removeMark(
                    change.pos,
                    change.pos + change.size,
                    change.markType
                  );
                }
              }

              dispatch(tr);
            }
          }

          return true;
        },

      acceptAllChanges:
        () =>
        ({ tr, state, dispatch }) => {
          const { doc, schema } = state;

          if (dispatch) {
            // Mark this transaction so onTransaction doesn't re-process deletions
            tr.setMeta("trackManualChanged", true);

            // Process deletions first (from end to start to preserve positions)
            const deletions: { pos: number; size: number }[] = [];

            doc.descendants((node, pos) => {
              node.marks.forEach(mark => {
                if (mark.type.name === "deletion") {
                  deletions.push({ pos, size: node.nodeSize });
                }
              });
            });

            // Sort by position descending
            deletions.sort((a, b) => b.pos - a.pos);

            // Delete from end to start
            for (const del of deletions) {
              tr.delete(del.pos, del.pos + del.size);
            }

            // Remove all insertion marks
            const insertionType = schema.marks.insertion;
            if (insertionType) {
              tr.removeMark(0, tr.doc.content.size, insertionType);
            }

            dispatch(tr);
          }

          return true;
        },

      rejectAllChanges:
        () =>
        ({ tr, state, dispatch }) => {
          const { doc, schema } = state;

          if (dispatch) {
            // Mark this transaction so onTransaction doesn't re-process deletions
            tr.setMeta("trackManualChanged", true);

            // Process insertions first (from end to start to preserve positions)
            const insertions: { pos: number; size: number }[] = [];

            doc.descendants((node, pos) => {
              node.marks.forEach(mark => {
                if (mark.type.name === "insertion") {
                  insertions.push({ pos, size: node.nodeSize });
                }
              });
            });

            // Sort by position descending
            insertions.sort((a, b) => b.pos - a.pos);

            // Delete from end to start
            for (const ins of insertions) {
              tr.delete(ins.pos, ins.pos + ins.size);
            }

            // Remove all deletion marks
            const deletionType = schema.marks.deletion;
            if (deletionType) {
              tr.removeMark(0, tr.doc.content.size, deletionType);
            }

            dispatch(tr);
          }

          return true;
        },

      setTrackChangesAuthor:
        (author: string) =>
        ({ editor }) => {
          const storage = (editor.storage as any).trackChanges;
          if (storage) {
            storage.author = author;
          }
          return true;
        },
    };
  },

  onSelectionUpdate() {
    const { onChangeActivated } = this.options;
    if (!onChangeActivated) return;

    const { state } = this.editor;
    const { from, to } = state.selection;

    let activeChangeId: string | null = null;
    let activeChangeType: "insertion" | "deletion" | null = null;
    let activeChangeAuthor: string | null = null;
    let activeChangeDate: string | null = null;

    state.doc.nodesBetween(from, to, node => {
      node.marks.forEach(mark => {
        if (mark.type.name === "insertion" || mark.type.name === "deletion") {
          activeChangeId = mark.attrs.changeId;
          activeChangeType = mark.type.name as "insertion" | "deletion";
          activeChangeAuthor = mark.attrs.author || null;
          activeChangeDate = mark.attrs.date || null;
        }
      });
    });

    onChangeActivated(
      activeChangeId,
      activeChangeType,
      activeChangeAuthor,
      activeChangeDate
    );
  },

  // Deletion handling moved entirely into appendTransaction (see addProseMirrorPlugins).
  // This makes delete + re-add atomic from the history plugin's perspective,
  // so Ctrl+Z correctly undoes both in a single step.

  addProseMirrorPlugins() {
    const extension = this;

    return [
      // ── Main plugin: handles insertion marks AND deletion re-adds ──
      new Plugin({
        key: trackChangesPluginKey,
        appendTransaction(transactions, oldState, newState) {
          // Skip during programmatic setContent() calls.  Other extensions
          // (e.g. pagination) may respond to the bulk content replacement
          // with structural transactions that don't set addToHistory:false,
          // which would incorrectly pass the isUserInput check below and
          // cause imported track-change marks to be stripped.
          if (extension.storage.suppressMarkStripping) {
            return null;
          }

          // Skip if this is a manual track change operation (accept/reject)
          if (transactions.some(tr => tr.getMeta("trackManualChanged"))) {
            return null;
          }

          // Skip undo/redo — let the history plugin work without interference
          if (transactions.some(tr => tr.getMeta("history$"))) {
            return null;
          }

          // Only process genuine user-input transactions
          const isUserInput = transactions.some(
            tr => tr.docChanged && tr.getMeta("addToHistory") !== false
          );
          if (!isUserInput) {
            return null;
          }

          const storage = extension.storage as TrackChangesStorage;
          const tr = newState.tr;
          const insertionMark = newState.schema.marks.insertion;
          const deletionMark = newState.schema.marks.deletion;
          let modified = false;
          let cursorPosition: number | null = null;

          // ── PHASE 1: Handle deletions (re-add with deletion marks) ──
          // Walk the original transactions' steps to find ReplaceSteps that
          // removed content. Re-add that content (minus already-tracked text)
          // with deletion marks in the SAME appendTransaction return value,
          // making it atomic with the original delete for undo purposes.
          if (storage.enabled && deletionMark) {
            for (const transaction of transactions) {
              if (!transaction.docChanged) continue;
              if (transaction.getMeta("addToHistory") === false) continue;
              if (transaction.getMeta("trackManualChanged")) continue;

              transaction.steps.forEach((step, index) => {
                if (!(step instanceof ReplaceStep)) return;
                if (step.from === step.to) return; // pure insertion

                const docBefore = transaction.docs[index];
                const slice = docBefore.slice(step.from, step.to);

                // Filter: skip nodes already tracked (insertion → truly delete,
                // deletion → already deleted, don't double-mark)
                const contentToReAdd: any[] = [];
                slice.content.forEach((node: any) => {
                  const hasInsertion = node.marks?.some(
                    (m: any) => m.type.name === "insertion"
                  );
                  const hasDeletion = node.marks?.some(
                    (m: any) => m.type.name === "deletion"
                  );
                  if (!hasInsertion && !hasDeletion) {
                    contentToReAdd.push(node);
                  }
                });

                if (contentToReAdd.length === 0) return;

                const changeId = getDeletionChangeId(storage, step.from);
                const author = storage.author || extension.options.author || undefined;
                const delMark = deletionMark.create({
                  changeId,
                  author,
                  date: new Date().toISOString(),
                });

                // Map the original step position into newState coordinates.
                // transaction.mapping covers all steps within THIS transaction;
                // we need to go from the pre-step position to post-transaction,
                // then account for any insertions we've already made in `tr`.
                const mappedPos = transaction.mapping.map(step.from, -1);
                const insertPos = tr.mapping.map(mappedPos, -1);

                // Build text nodes preserving original formatting + adding deletion mark
                const nodesToInsert: any[] = [];
                for (const node of contentToReAdd) {
                  const text = node.isText ? (node.text || "") : (node.textContent || "");
                  if (!text) continue;
                  const existingMarks = node.isText && node.marks ? [...node.marks] : [];
                  const filteredMarks = existingMarks.filter(
                    (m: any) => m.type.name !== "insertion"
                  );
                  nodesToInsert.push(
                    newState.schema.text(text, [...filteredMarks, delMark])
                  );
                }

                if (nodesToInsert.length === 0) return;

                let insertOffset = 0;
                for (const textNode of nodesToInsert) {
                  tr.insert(insertPos + insertOffset, textNode);
                  insertOffset += textNode.nodeSize;
                }

                cursorPosition = insertPos;
                updateDeletionTracking(storage, step.from);
                modified = true;
              });
            }

            // Position cursor before the re-added deletion-marked text and
            // set stored marks to exclude the deletion mark so typing
            // doesn't inherit red strikethrough.
            if (cursorPosition !== null) {
              try {
                const mappedCursor = tr.mapping.map(cursorPosition, -1);
                const clampedCursor = Math.min(mappedCursor, tr.doc.content.size);
                const selection = TextSelection.create(tr.doc, clampedCursor);
                tr.setSelection(selection);
                const cursorMarks = selection.$from.marks();
                const cleanMarks = cursorMarks.filter(
                  (m: any) => m.type !== deletionMark
                );
                tr.setStoredMarks(cleanMarks);
              } catch {
                // Position mapping failed; leave cursor where ProseMirror put it
              }
            }
          }

          // ── PHASE 2: Handle insertions (add insertion marks to new text) ──
          const newRanges: { from: number; to: number }[] = [];

          for (const transaction of transactions) {
            if (!transaction.docChanged) continue;
            if (transaction.getMeta("addToHistory") === false) continue;

            for (const step of transaction.steps) {
              if (
                step instanceof ReplaceStep ||
                step instanceof ReplaceAroundStep
              ) {
                const stepMap = step.getMap();
                stepMap.forEach((_oldStart, _oldEnd, newStart, newEnd) => {
                  if (newEnd > newStart) {
                    // Map these positions through Phase 1's changes
                    const mappedFrom = tr.mapping.map(newStart);
                    const mappedTo = tr.mapping.map(newEnd);
                    if (mappedTo > mappedFrom) {
                      newRanges.push({ from: mappedFrom, to: mappedTo });
                    }
                  }
                });
              }
            }
          }

          if (newRanges.length > 0) {
            if (storage.enabled) {
              // Tracking is ON: Add insertion marks to new text
              if (insertionMark) {
                for (const range of newRanges) {
                  // Safety net: remove any deletion marks that may have bled into
                  // newly typed text (e.g. from mark inheritance at boundaries).
                  if (deletionMark) {
                    tr.removeMark(range.from, range.to, deletionMark);
                    modified = true;
                  }
                  const changeId = getInsertionChangeId(storage, range.from);
                  const author = storage.author || extension.options.author || undefined;
                  const mark = insertionMark.create({
                    changeId,
                    author,
                    date: new Date().toISOString(),
                  });
                  tr.addMark(range.from, range.to, mark);
                  updateInsertionTracking(storage, range.to);
                  modified = true;
                }
              }
            } else {
              // Tracking is OFF: Remove any insertion/deletion marks from new text
              resetInsertionTracking(storage);
              for (const range of newRanges) {
                if (insertionMark) {
                  tr.removeMark(range.from, range.to, insertionMark);
                  modified = true;
                }
                if (deletionMark) {
                  tr.removeMark(range.from, range.to, deletionMark);
                  modified = true;
                }
              }
            }
          }

          return modified ? tr : null;
        },
      }),

      // ── Navigation plugin: cursor skips over deletion-marked text ──
      new Plugin({
        key: trackChangesNavPluginKey,

        props: {
          // Make deletion-marked text unselectable via decorations
          decorations(state) {
            if (!(extension.storage as TrackChangesStorage).enabled) {
              return DecorationSet.empty;
            }
            const delType = state.schema.marks.deletion;
            if (!delType) return DecorationSet.empty;

            const decorations: Decoration[] = [];
            state.doc.descendants((node, pos) => {
              if (node.isText && node.marks.some(m => m.type === delType)) {
                decorations.push(
                  Decoration.inline(pos, pos + node.nodeSize, {
                    class: "track-change-deleted-phantom",
                  })
                );
              }
            });
            return decorations.length
              ? DecorationSet.create(state.doc, decorations)
              : DecorationSet.empty;
          },

          // Arrow keys skip over deletion-marked ranges
          handleKeyDown(view, event) {
            if (!(extension.storage as TrackChangesStorage).enabled) return false;
            if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return false;

            const { state } = view;
            const { from, to } = state.selection;
            const delType = state.schema.marks.deletion;
            if (!delType) return false;

            // Only intercept for collapsed cursor (not selection extension)
            if (from !== to) return false;
            if (event.shiftKey) return false;

            const dir = event.key === "ArrowRight" ? 1 : -1;
            let pos = from + dir;

            // Walk in direction, skipping contiguous deletion-marked positions
            const maxPos = state.doc.content.size;
            while (pos > 0 && pos < maxPos) {
              const $pos = state.doc.resolve(pos);
              // Check marks at this text position
              const nodeAfter = dir > 0 ? $pos.nodeAfter : $pos.nodeBefore;
              if (!nodeAfter || !nodeAfter.marks.some(m => m.type === delType)) {
                break;
              }
              pos += dir;
            }

            // Clamp
            pos = Math.max(0, Math.min(pos, maxPos));

            if (pos === from) return false; // nowhere to go

            const newTr = state.tr.setSelection(
              TextSelection.create(state.doc, pos)
            );
            view.dispatch(newTr);
            return true;
          },

          // Clicking inside deleted text snaps cursor to the nearest edge
          handleClick(view, pos) {
            if (!(extension.storage as TrackChangesStorage).enabled) return false;
            const { state } = view;
            const delType = state.schema.marks.deletion;
            if (!delType) return false;

            // Check if clicked position is inside deletion-marked text
            const $pos = state.doc.resolve(pos);
            const nodeAtPos = $pos.nodeAfter || $pos.nodeBefore;
            if (!nodeAtPos || !nodeAtPos.marks.some(m => m.type === delType)) {
              return false;
            }

            // Walk forward to find first non-deleted position
            const maxPos = state.doc.content.size;
            let forward = pos;
            while (forward < maxPos) {
              const $f = state.doc.resolve(forward);
              const n = $f.nodeAfter;
              if (!n || !n.marks.some(m => m.type === delType)) break;
              forward++;
            }

            // If forward didn't move far, try backward
            let target = forward;
            if (forward >= maxPos) {
              let backward = pos;
              while (backward > 0) {
                const $b = state.doc.resolve(backward);
                const n = $b.nodeBefore;
                if (!n || !n.marks.some(m => m.type === delType)) break;
                backward--;
              }
              target = backward;
            }

            target = Math.max(0, Math.min(target, maxPos));
            const newTr = state.tr.setSelection(
              TextSelection.create(state.doc, target)
            );
            view.dispatch(newTr);
            return true;
          },
        },
      }),
    ];
  },
});

/**
 * Get all track changes in the document
 */
export function getTrackChanges(editor: any): TrackChangeInfo[] {
  const changes: TrackChangeInfo[] = [];
  const { doc } = editor.state;

  doc.descendants((node: any, pos: number) => {
    node.marks.forEach((mark: any) => {
      if (mark.type.name === "insertion" || mark.type.name === "deletion") {
        changes.push({
          id: mark.attrs.changeId,
          type: mark.type.name as "insertion" | "deletion",
          from: pos,
          to: pos + node.nodeSize,
          text: node.text || "",
          author: mark.attrs.author || undefined,
          date: mark.attrs.date || undefined,
        });
      }
    });
  });

  // Remove duplicates (same changeId might span multiple nodes)
  const uniqueChanges = new Map<string, TrackChangeInfo>();
  for (const change of changes) {
    if (!uniqueChanges.has(change.id)) {
      uniqueChanges.set(change.id, change);
    } else {
      // Extend the existing change
      const existing = uniqueChanges.get(change.id)!;
      existing.to = Math.max(existing.to, change.to);
      existing.text += change.text;
      // Keep author/date from first occurrence (they should be the same)
    }
  }

  return Array.from(uniqueChanges.values());
}

export default TrackChangesExtension;
